import {
  OrderDocExtractionQuery,
  OrderDocExtractionStatus as Status,
  useExtractOrderDocMutation,
  useOrderDocExtractionQuery,
} from '@/buyers/_gen/gql'
import useGqlClient from '@/buyers/hooks/useGqlClient'
import useMsgs from '@/gf/hooks/useMsgs'
import { useEffect, useState } from 'react'

type OrderDoc = NonNullable<NonNullable<OrderDocExtractionQuery['orderDocExtraction']>['orderDoc']>

const useUpload = (
  onExtracted: (orderDoc: OrderDoc, documentId: string) => void,
  {
    // An in-flight extraction is resumed from localStorage, so each page that
    // uploads needs its own key — otherwise Enter Quote would pick up a document
    // still being extracted for Log an Order, and vice versa (GEA-6418).
    storageKey = 'order-doc-extraction-id',
    notFoundMessage = 'No order details were found in the file.',
  }: { storageKey?: string; notFoundMessage?: string } = {}
) => {
  const msgr = useMsgs()[1]
  const client = useGqlClient()
  const [extract] = useExtractOrderDocMutation({ client })
  const [uploading, setUploading] = useState(false)
  const [extractionId, extractionIdDispatch] = useState(() => localStorage.getItem(storageKey))
  // The uploaded document exists from the moment `extractOrderDoc` returns, whatever
  // extraction then makes of it. Holding the id here — rather than handing it out only
  // on a successful extraction — is what lets a caller keep the file on the record when
  // the buyer types the prices in by hand after a failed read (GEA-6418).
  //
  // Seeded from the same stored value as `extractionId` because they are the same id:
  // `extract_order_doc` returns the Document's id and `orderDocExtraction(id:)` looks the
  // Document up by it. Without this, reloading mid-extraction would resume the poll but
  // lose the document.
  const [documentId, setDocumentId] = useState<string | null>(() =>
    localStorage.getItem(storageKey)
  )

  const { data } = useOrderDocExtractionQuery({
    variables: { id: extractionId! },
    client,
    pollInterval: 2000,
    skip: !extractionId,
  })

  const setExtractionId = (id: string | null) => {
    if (id === null) localStorage.removeItem(storageKey)
    else localStorage.setItem(storageKey, id)

    extractionIdDispatch(id)
  }

  // Stop polling. The document stays uploaded and `documentId` stands — extraction is
  // done with, one way or the other.
  const resetExtraction = () => {
    setExtractionId(null)
    setUploading(false)
  }

  // The buyer removed the file, so the document goes with it.
  const resetUpload = () => {
    resetExtraction()
    setDocumentId(null)
  }

  useEffect(() => {
    if (!data) return

    const extraction = data.orderDocExtraction

    if (!extraction) {
      resetUpload()
      return
    }

    if (extraction.status === Status.Succeeded || extraction.status === Status.PartialSuccess) {
      onExtracted(extraction.orderDoc!, extractionId!)
      resetExtraction()
    }

    if (extraction.status === Status.Failed) {
      msgr.add(notFoundMessage, 'neutral')
      resetExtraction()
    }
  }, [data])

  const extractOrderDoc = async (url: string) => {
    const { data } = await extract({ variables: { url } })
    setUploading(false)
    setDocumentId(data!.extractOrderDoc)
    setExtractionId(data!.extractOrderDoc)
  }

  return {
    uploadProcessing: uploading || extractionId,
    uploadStarted: () => setUploading(true),
    resetUpload,
    extractOrderDoc,
    documentId,
  }
}

export default useUpload
